import React, { useCallback, useRef, useState } from 'react';
import {
  StyleSheet,
  View,
  Text,
  Pressable,
  ScrollView,
  useWindowDimensions,
  Dimensions,
} from 'react-native';
import { useNavigate } from 'react-router-dom';

import { Map } from 'components/Map';
import { ErrorNotification } from 'components/reusable/ErrorNotification';
import Popup from 'components/reusable/Popup';
import { Routes } from 'components/admin/Routes';
import { UserReports } from 'components/admin/UserReports';
import { Report } from 'types/reports';
import { Notifications } from 'components/admin/Notifications';
import { Markers } from 'components/admin/Markers';
import { Areas } from 'components/admin/Areas';
import { logout } from 'data/user';
import { add as addNotification } from 'hooks/notificationsSlice';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { addCoords } from 'hooks/editSlice';
import { LayerToggle } from 'components/map-buttons/LayerToggle';

export function Admin() {
  const navigate = useNavigate();
  const { height, width } = useWindowDimensions();
  const styles = getStyles(width);

  const dispatch = useAppDispatch();
  const markers = useAppSelector((state) => state.markers);
  const routes = useAppSelector((state) => state.routes);
  const areas = useAppSelector((state) => state.areas);
  const reports = useAppSelector((state) => state.reports);
  const edit = useAppSelector((state) => state.edit);

  const [error, setError] = useState(null);
  const [popup, setPopup] = useState(null);
  const [isSatelliteView, setIsSatelliteView] = useState(false);

  const refScrollView = useRef(null);
  const notificationView = useRef(null);
  const terrainRef = useRef(null);

  function onPress(loc: { lat: number; lng: number }) {
    if (edit.value.editing) {
      const newArray = edit.value.coords ? [...edit.value.coords] : [];
      newArray.push([loc.lat, loc.lng]);
      dispatch(addCoords({ coords: newArray }));
    }
  }

  const convertReportToNotification = useCallback(
    (report: Report) => {
      dispatch(
        addNotification({
          notification: {
            id: 'NEW',
            title: '',
            description: report.description,
            level: 0,
            timestamp: Date.now(),
          },
        }),
      );
      setTimeout(() => {
        notificationView.current.measureInWindow(
          (x: number, y: number, w: number, h: number) => {
            refScrollView.current.scrollTo({
              x,
              y: y + h - 60 - Dimensions.get('window').height,
              animated: true,
            });
          },
        );
      }, 500);
    },
    [dispatch],
  );

  function signout() {
    logout()
      .then(() => {
        return navigate('/login');
      })
      .catch(() => {
        setError('Unable to log out, please try again later.');
      });
  }

  return (
    <View style={styles.container}>
      <ScrollView
        style={[
          styles.inputsContainer,
          width >= 900
            ? { height: height - 60 }
            : mobileStyles(height).container,
        ]}
        ref={refScrollView}
      >
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Admin Panel</Text>
          <Pressable style={styles.button} onPress={signout}>
            <Text style={styles.buttonText}>Log out</Text>
          </Pressable>
        </View>
        <UserReports
          styles={styles}
          addNotification={convertReportToNotification}
          displayError={setError}
          confirmAction={setPopup}
        />
        <View ref={notificationView}>
          <Notifications
            styles={styles}
            displayError={setError}
            confirmAction={setPopup}
          />
        </View>
        <Markers
          styles={styles}
          displayError={setError}
          confirmAction={setPopup}
        />
        <Routes
          styles={styles}
          displayError={setError}
          confirmAction={setPopup}
        />
        <Areas
          styles={styles}
          displayError={setError}
          confirmAction={setPopup}
        />

        {error && (
          <ErrorNotification
            title="Error"
            text={error}
            close={() => setError(null)}
            align="bottom"
          />
        )}
      </ScrollView>
      {popup && (
        <Popup
          align="fixed"
          title={popup.title}
          body={`Are you sure you want to delete ${popup.name}?`}
          buttons={[
            {
              text: 'Delete',
              action: popup.delete,
            },
            {
              text: 'Cancel',
              action: () => setPopup(null),
            },
          ]}
          dismiss={() => setPopup(null)}
        />
      )}
      <View
        style={[
          styles.mapContainer,
          width >= 900
            ? { height: height - 60 }
            : mobileStyles(height).container,
        ]}
      >
        <Map
          onPress={onPress}
          markers={markers.value}
          routes={
            routes.value.find((r) => r.id === 'NEW')
              ? [routes.value.find((r) => r.id === 'NEW')]
              : routes.value
          }
          areas={areas.value}
          isSatelliteView={isSatelliteView}
          reports={reports.value}
        />

        <View style={styles.buttonContainerLeft}>
          <LayerToggle
            ref={terrainRef}
            onPress={() => setIsSatelliteView(!isSatelliteView)}
            state={isSatelliteView}
          />
        </View>
      </View>
    </View>
  );
}

function mobileStyles(height) {
  return StyleSheet.create({
    container: {
      height: (height - 60) / 2,
      // @ts-ignore
      flex: 'auto',
    },
  });
}

function getStyles(width) {
  return StyleSheet.create({
    container: {
      width: '100%',
      flexDirection: width >= 900 ? 'row' : 'column-reverse',
    },
    inputsContainer: {
      flex: 1,
      padding: 20,
      overflowY: 'auto',
    },
    mapContainer: {
      flex: 1,
      overflow: 'hidden',
    },
    button: {
      backgroundColor: '#152B61',
      alignSelf: 'flex-start',
    },
    buttonText: {
      color: '#fff',
      fontWeight: 'bold',
      paddingHorizontal: 15,
      paddingVertical: 10,
      fontSize: 16,
    },
    titleContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignContent: 'center',
      alignItems: 'center',
      marginBottom: 20,
    },
    title: {
      fontSize: 24,
      fontWeight: 'bold',
    },
    subtitle: {
      fontSize: 20,
    },
    flipped: {
      transform: 'rotate(180deg)',
    },
    collapsible: {
      marginBottom: 10,
    },
    collapsibleButton: {
      backgroundColor: '#152B61',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 5,
    },
    collapsibleButtonText: {
      fontSize: 18,
      color: '#fff',
      fontWeight: 'bold',
      padding: 10,
    },
    collapsibleContainer: {
      borderWidth: 1,
      borderColor: '#152B61',
    },
    row: {
      borderBottomWidth: 1,
      borderBottomColor: '#152B61',
      padding: 10,
    },
    buttonContainer: {
      padding: 10,
    },
    dropDownArrow: {
      width: 40,
      height: 40,
    },
    image: {
      width: 100,
      height: 100,
    },
    icon: {
      width: 40,
      height: 40,
    },
    editIcon: {
      width: 20,
      height: 20,
    },
    editContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    inlineButtonContainer: {
      flexDirection: 'row',
      marginTop: 10,
    },
    inlineButton: {
      color: '#152B61',
      fontWeight: 'bold',
      marginRight: 5,
    },
    input: {
      borderWidth: 1,
      borderColor: 'grey',
      padding: 5,
      borderRadius: 0,
      marginBottom: 10,
    },
    success: {
      color: '#008000',
    },
    error: {
      color: '#e43333',
    },
    inputError: {
      borderColor: '#e43333',
      marginBottom: 0,
    },
    label: {
      fontSize: 18,
      marginBottom: 5,
      fontWeight: 600,
    },
    coords: {
      flexDirection: 'row',
    },
    disabled: {
      opacity: 0.5,
    },
    action: {
      fontWeight: 'bold',
      color: '#fa9300',
    },
    buttonContainerLeft: {
      position: 'absolute',
      zIndex: 999,
      left: 0,
      top: 75,
      padding: 10,
    },
  });
}
